import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Scale, Shield, Award, Users } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function CopyrightLegalRepresentation() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Scale, title: 'Expert Litigation', description: 'Experienced attorneys for copyright infringement lawsuits.' },
    { icon: Shield, title: 'Defense Services', description: 'Defend against claims of copyright infringement.' },
    { icon: Award, title: 'Maximum Recovery', description: 'Pursue statutory damages and attorney fees.' },
    { icon: Users, title: 'Settlement Negotiation', description: 'Negotiate favorable settlements when appropriate.' },
  ];

  const faqs = [
    { question: 'When do I need legal representation for copyright?', answer: 'You need representation when facing copyright infringement litigation, defending against infringement claims, or when cease and desist efforts have failed.' },
    { question: 'What damages can I recover?', answer: 'If your work is registered before infringement, you can recover statutory damages ($750-$150,000 per work) plus attorney fees, or actual damages and profits.' },
    { question: 'How long does copyright litigation take?', answer: 'Cases can take 1-3 years or more, depending on complexity. Many cases settle before trial, which can significantly reduce time and costs.' },
    { question: 'Do I need to register my copyright before suing?', answer: 'Yes, U.S. law requires copyright registration before filing an infringement lawsuit, though registration can occur after the infringement began.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1589391886645-d51941baf7fb?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>Legal Representation for Copyright</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Expert legal representation for copyright litigation and disputes.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our Legal Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Experienced copyright litigation attorneys on your side.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about copyright legal representation.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Need Copyright Legal Representation?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Protect your rights with experienced copyright litigation attorneys.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="copyright_legal" />
      <Footer />
    </div>
  );
}